import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    ListExpensesParams,
    UpsertExpenseRequest,
} from '@/lib/api/expense-api'
import { expenseApi } from '@/lib/api/expense-api'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

export const expenseKeys = {
    all: ['expenses'] as const,
    lists: () => [...expenseKeys.all, 'list'] as const,
    list: (params: ListExpensesParams) =>
        [...expenseKeys.lists(), params] as const,
    detail: (id: string) => [...expenseKeys.all, 'detail', id] as const,
}

export function useExpenses(params?: ListExpensesParams) {
    return useQuery({
        queryKey: expenseKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await expenseApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
        // Đổi trang/bộ lọc là đổi queryKey. Giữ kết quả cũ trong lúc chờ để
        // bảng không nháy sang trạng thái rỗng và thanh phân trang không biến
        // mất ngay khi vừa bấm sang trang.
        placeholderData: keepPreviousData,
    })
}

export function useExpense(id: string) {
    return useQuery({
        queryKey: expenseKeys.detail(id),
        queryFn: async () => {
            const res = await expenseApi.getById(id)
            return res.data ?? null
        },
        enabled: Boolean(id),
    })
}

export function useCreateExpense() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpsertExpenseRequest) => expenseApi.create(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: expenseKeys.lists() })
            toast.success(m.toast_expense_save_success())
        },
        onError: () => toast.error(m.toast_expense_save_error()),
    })
}

export function useUpdateExpense(id: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpsertExpenseRequest) => expenseApi.update(id, data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: expenseKeys.lists() })
            void qc.invalidateQueries({ queryKey: expenseKeys.detail(id) })
            toast.success(m.toast_expense_save_success())
        },
        onError: () => toast.error(m.toast_expense_save_error()),
    })
}

export function useDeleteExpense() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => expenseApi.delete(id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: expenseKeys.lists() })
            toast.success(m.toast_expense_delete_success())
        },
        onError: () => toast.error(m.toast_expense_delete_error()),
    })
}

// "Không trùng" — resolves the "Có thể trùng" flag without touching anything
// else on the row.
export function useClearPossibleDuplicate() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => expenseApi.clearPossibleDuplicate(id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: expenseKeys.lists() })
            toast.success(m.toast_expense_clear_duplicate_success())
        },
        onError: (err) => {
            const payload = formatApiError(
                err,
                m.toast_expense_clear_duplicate_error(),
            )
            toast.error(payload.title, { description: payload.description })
        },
    })
}

// Candidates for "Gộp vào khoản nhập tay": manual expenses sharing the
// bank-sourced expense's date+amount. Enabled only while the merge dialog
// is open (id passed in), same pattern as the old expense-sync candidates
// query it replaces.
export function useMergeCandidates(autoExpenseId: string) {
    return useQuery({
        queryKey: [...expenseKeys.all, 'merge-candidates', autoExpenseId],
        queryFn: async () => {
            const res = await expenseApi.mergeCandidates(autoExpenseId)
            return res.data ?? []
        },
        enabled: Boolean(autoExpenseId),
    })
}

// "Gộp vào khoản nhập tay" — folds the auto-created bank expense into an
// existing manual one; the manual row takes over the bank link.
export function useMergeIntoManual() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            autoExpenseId,
            manualExpenseId,
        }: {
            autoExpenseId: string
            manualExpenseId: string
        }) => expenseApi.mergeIntoManual(autoExpenseId, manualExpenseId),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: expenseKeys.lists() })
            toast.success(m.toast_expense_merge_success())
        },
        onError: (err) => {
            const payload = formatApiError(err, m.toast_expense_merge_error())
            toast.error(payload.title, { description: payload.description })
        },
    })
}

// List page's bulk "Đổi danh mục" — every selected row becomes category_source='manual'.
export function useBulkSetExpenseCategory() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            ids,
            categoryId,
        }: {
            ids: string[]
            categoryId: string
        }) => expenseApi.bulkSetCategory(ids, categoryId),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: expenseKeys.lists() })
            toast.success(m.toast_expense_bulk_category_success())
        },
        onError: (err) => {
            const payload = formatApiError(
                err,
                m.toast_expense_bulk_category_error(),
            )
            toast.error(payload.title, { description: payload.description })
        },
    })
}
